import ScaleBar from '@arcgis/core/widgets/ScaleBar';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { MAP_ATTRIBUTION_GEOCOMM } from '@rsos/capstone/src/constants/capabilities';
import { withLayout } from '@rsos/components/capstone/base/Layout';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { initializeMap } from '@rsos/maps';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { normalizeExtent } from '@rsos/maps/utils/utils';
import { selectGISServices, selectGISLayers } from '@rsos/reduxDataStore';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { DARK_THEME } from '@rsos/theme/constants';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import { useIsMapIconDisabled } from '@rsos/utils/useIsMapIconDisabled';
import usePortalType from '@rsos/utils/usePortalType';
import useEsriMapTransition from '../../../hooks/useEsriMapTransition';
import { useGISLayersOpacity } from '../../../hooks/useGISLayersOpacity';
import { useGISLayersViewDebounce } from '../../../hooks/useGISLayersViewDebounce';
import useReorderEsriMapMarkers from '../../../hooks/useReorderEsriMapMarkers';
import { RecenterMapButton } from '../../Map/RecenterMapButton';
import DeckGLNext from '../../Map/useDeckGL/DeckGLNext';
import MapCard from '../../MapCard';
import { EsriMapsContext } from './EsriMapsProvider';
import GeoCommIndoorMaps from './GeoCommIndoorMaps';
import TimeSliderComponent from './TimeSlider/TimeSlider';
import useDrawERA from './hooks/useDrawEra';
import useGISToken from './hooks/useGISToken';
import { W3WMarkers } from './markers/W3WMarkers';
import AllAlertMarkers from './markers/alerts/AllAlertMarkers';
import { CallMarkers } from './markers/calls/CallMarkers';
import { getMapsApiKey, updateAttributionText } from './utils';

/**
 * Click the scale bar to toggle imperial (default) and metric.
 * @param {*} scaleBar - Esri ScaleBar instance
 * @param {*} view - Esri MapView
 */
function attachScaleBarUnitToggle(scaleBar, view) {
  const onClick = event => {
    event.stopPropagation();
    scaleBar.unit = scaleBar.unit === 'metric' ? 'non-metric' : 'metric';
  };

  const tryBind = (attemptsLeft = 40) => {
    const raw = scaleBar.container ?? scaleBar.domNode;
    const el = typeof raw === 'string' ? document.getElementById(raw) : raw;
    if (el instanceof HTMLElement) {
      el.style.cursor = 'pointer';
      el.setAttribute(
        'title',
        'Click to switch between imperial and metric units',
      );
      el.addEventListener('click', onClick);
      return;
    }
    if (attemptsLeft > 0) {
      requestAnimationFrame(() => tryBind(attemptsLeft - 1));
    }
  };

  view.when().then(() => tryBind());
}

const MapWrapper = withLayout(styled.div`
  height: 100%;
  width: 100%;
  ${({ isResizing }) => `${isResizing ? 'pointer-events: none;' : ''}`}
`);

const StyledMap = styled.div`
  width: 100%;
  height: 100%;
`;

const EsriDarkTheme = lazy(
  () => import('@rsos/maps/providers/esri/themes/DarkTheme'),
);

const GEOCOMM_INDOOR_MAPS_PREFIX = 'geocomm-indoor-maps__';

/**
 * @param {String} activeQueueTab - ALERTS or EMERGENCY_CALLS
 * @param {Object} currentERA - ERA data
 * @param {String} hoveredCall - Phone number of the hovered call
 * @param {Function} onMapReady - Callback function when Map is ready for JV
 * @param {Function} onMarkerAdd - Callback function when a new marker is drawn
 * @param {Function} onMarkerClick - onClick handler for markers
 * @param {Function} onMarkerHover - Hover effect handler for markers
 * @param {Function} onRemoveID - Function to set state in JV for removed alert
 * @param {Function} setMafError - Function to set medicalert error
 * @param {Function} setADRError - Function to set adr error
 * @param {String} themeName - Theme name used to switch Google maps theme
 */

/**
 * ESRI Map
 */
export const Map = ({
  activeQueueTab,
  currentERA,
  hoveredCall,
  onMapReady,
  onMarkerAdd,
  onMarkerClick,
  onMarkerHover,
  onRemoveID,
  removedID,
  setMafError,
  setADRError,
  themeName,
  triggerViewJurisdiction,
}) => {
  const { setMap: setMapForContext } = useZoomAndLocationContext();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const { exact_polygon_center, shapes = [] } = useSelector(
    state => state.geofences.currentGeofence,
  );

  const { isPremiumUser } = usePortalType();
  const alerts = useSelector(state => filteredAlertsSelector(state.incidents));

  const { hoveredAlertID, hoveredFrom } = useSelector(
    state => state.hoveredIncident,
  );

  const hoveredAlert = alerts[hoveredAlertID];

  const hasW3W = useSelector(state => state.what3words.searched?.coordinates);

  const capabilities = useSelector(
    state => state.psaps.currentPSAP.activeCapabilities,
  );

  const gisServices = useSelector(selectGISServices);
  const alternativeBasemapService = useMemo(() => {
    return gisServices.find(service => {
      return service.attributes?.gis_service_category === 'alternative_basemap';
    });
  }, [gisServices]);

  const isMapIconDisabled = useIsMapIconDisabled();

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasGISServicesAccess =
    useSelector(state =>
      checkGISServicesAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && hasEsriMapAccess;

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const mapApiKey = getMapsApiKey(mapProvider);

  const loadMap = useCallback(() => {
    initializeMap({
      mapProvider,
      apiKey: mapApiKey,
      mapOptions: {
        mapContainer: mapRef.current,
        center: {
          lat: exact_polygon_center[1],
          lng: exact_polygon_center[0],
        },
        shapes: shapes,
        basemap: basemap,
        hasAlternativeBasemap: Boolean(alternativeBasemapService),
        alternativeBasemapItemIds:
          alternativeBasemapService?.attributes?.map_id,
      },
      viewOptions: {
        popup: {
          actions: [],
          autoOpenEnabled: false,
          dockEnabled: false,
          dockOptions: {
            buttonEnabled: false,
            breakpoint: false,
          },
          maxInlineActions: 0,
        },
      },
    }).then(map => {
      exportToGlobalNamespace('map', map);
      setMap(map);
      setMapForContext(
        map.mapInstance
          ? { mapProvider: ESRI, mapInstance: map.mapInstance }
          : null,
      );
      const geocommAttribution = capabilities[MAP_ATTRIBUTION_GEOCOMM];
      // check geocomm capability
      if (geocommAttribution && gisServices.length && hasGISServicesAccess) {
        updateAttributionText({ map, partnerText: 'GeoComm' });
      }
      // Zoom bottom-right; scale bar starts imperial — click toggles metric/imperial
      const view = map.mapInstance.view;
      view.ui.move(['zoom'], 'bottom-right');
      const scaleBar = new ScaleBar({
        view,
        unit: 'non-metric',
        style: 'line',
      });
      view.ui.add(scaleBar, 'bottom-right');
      attachScaleBarUnitToggle(scaleBar, view);
      onMapReady();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store map instance in ref for cleanup access on unmount
  const mapInstanceForCleanupRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) {
      loadMap();
    }
  }, [loadMap, mapRef]);

  // Keep ref updated with latest map instance
  useEffect(() => {
    mapInstanceForCleanupRef.current = map?.mapInstance;
  }, [map]);

  // Cleanup MapView and Map resources on unmount only to prevent memory leaks
  // This is critical for Windows users with limited RAM (8GB)
  useEffect(() => {
    return () => {
      const mapInstance = mapInstanceForCleanupRef.current;
      if (mapInstance) {
        try {
          // Destroy view first, then map
          mapInstance.view?.destroy();
          mapInstance.map?.destroy();
        } catch (error) {
          // Suppress errors during cleanup - map may already be destroyed
          // eslint-disable-next-line no-console -- error visibility and Sentry breadcrumbs
          console.warn('Map cleanup warning:', error);
        }
      }
    };
    // Empty deps - only run cleanup on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
  This effect is used to disable pointer events on the map when resizing. This fixes an issue with the
  map crashing due to a race condition relating to deck.gl layer picking events while resizing. Dynamically
  disabling pickable in the deck.gl layers still causes issues in some layers, so this is a workaround.
  Check to see if we can remove this once we upgrade to newer versions of deck.gl
  (currently using deck.gl@8.9.7)
  */
  useEffect(() => {
    const view = map?.mapInstance?.view;

    if (!view) return;
    let stationaryHandle;
    stationaryHandle = view.watch('resizing', newValue => {
      setIsResizing(newValue);
    });

    return () => {
      if (stationaryHandle) {
        stationaryHandle.remove();
      }
    };
  }, [map]);

  /**
   * Reset the map viewport back to the jurisdiction area.
   */
  useEffect(() => {
    if (map?.mapInstance && !hasW3W) {
      map.mapInstance.view.extent = normalizeExtent(shapes);
    }
  }, [hasW3W, map?.mapInstance, shapes, triggerViewJurisdiction]);

  const handleCallMarkerAdd = () => {
    onMarkerAdd(EMERGENCY_CALLS);
  };

  const handleCallMarkerClick = (callerID, isAllView) => {
    onMarkerClick(callerID, EMERGENCY_CALLS, isAllView);
  };

  const handleCallMarkerHover = (callerID, secondary) => {
    onMarkerHover(callerID, 'map', EMERGENCY_CALLS, secondary);
  };

  const handleCallMarkerRemove = callerID => {
    onRemoveID(callerID);
  };

  useDrawERA(map, currentERA);
  useGISToken();
  const gisServiceLayers = useSelector(selectGISLayers);
  const gisServicesLoading = useSelector(state => state.gisServices?.loading);

  useGISLayersOpacity(map?.mapInstance?.map);
  const shouldPauseSupplementalLayers = useGISLayersViewDebounce(
    map?.mapInstance,
  );
  useEsriMapTransition(map);
  useReorderEsriMapMarkers(map);

  const loadedLayers = useMemo(
    () => gisServiceLayers.filter(l => l.loaded && l.gisLayer),
    [gisServiceLayers],
  );

  const loadedLayerIds = useMemo(
    () => loadedLayers.map(l => l.id).join(','),
    [loadedLayers],
  );
  // Tracks GIS layer ids we previously mounted onto the ESRI map so we can
  // unmount any that disappear from Redux (e.g. deleted on-demand layers).
  const mountedGISLayerIdsRef = useRef(new Set());

  // Pre-compute supplemental layer IDs (non-GeoComm layers) for pan/zoom debouncing
  const supplementalLayerIds = useMemo(() => {
    const ids = new Set();
    for (const layer of gisServiceLayers) {
      const url = layer.service?.gis_service_url?.toLowerCase();
      if (url && !url.includes('geocomm')) {
        ids.add(layer.id);
      }
    }
    return ids;
  }, [gisServiceLayers]);

  // Track previous pause state to detect transition from paused -> active
  const wasPausedRef = useRef(false);

  // Toggle LayerView visibility during pan/zoom to reduce API queries
  // Using layerView.visible is more performant than layer.visible
  // When resuming, call refresh() to fetch fresh data for the new extent
  useEffect(() => {
    const esriMap = map?.mapInstance?.map;
    const esriView = map?.mapInstance?.view;
    if (!esriMap || !esriView?.layerViews || supplementalLayerIds.size === 0) {
      return;
    }

    const isResuming = wasPausedRef.current && !shouldPauseSupplementalLayers;
    wasPausedRef.current = shouldPauseSupplementalLayers;

    for (const layerId of supplementalLayerIds) {
      const layer = esriMap.findLayerById(layerId);
      if (!layer) continue;

      // Find the Redux state for this layer to get user's intended visibility
      const layerState = gisServiceLayers.find(l => l.id === layerId);
      const userWantsVisible = layerState?.active ?? false;

      // Toggle visibility at the LayerView level (more performant than layer.visible)
      const layerView = esriView.layerViews.find(
        lv => lv.layer?.id === layerId,
      );
      if (layerView) {
        layerView.visible = userWantsVisible && !shouldPauseSupplementalLayers;
      }

      // When resuming from pause, explicitly refresh to fetch data for new extent
      if (
        isResuming &&
        userWantsVisible &&
        typeof layer.refresh === 'function'
      ) {
        layer.refresh();
      }
    }
  }, [
    map?.mapInstance?.map,
    map?.mapInstance?.view,
    supplementalLayerIds,
    shouldPauseSupplementalLayers,
    gisServiceLayers,
  ]);

  useEffect(() => {
    if (gisServicesLoading) {
      return;
    }

    const esriMap = map?.mapInstance?.map;
    if (!esriMap) {
      return;
    }

    const nextLoadedLayers = hasGISServicesAccess ? loadedLayers : [];
    const nextLoadedLayerIds = new Set(nextLoadedLayers.map(l => l.id));

    // Unmount layers that were previously mounted but are no longer present in
    // Redux (for example after removing an on-demand layer).
    mountedGISLayerIdsRef.current.forEach(layerId => {
      if (nextLoadedLayerIds.has(layerId)) return;
      const staleLayer = esriMap.findLayerById(layerId);
      if (staleLayer) {
        esriMap.remove(staleLayer);
      }
    });

    mountedGISLayerIdsRef.current = nextLoadedLayerIds;
    if (!nextLoadedLayers.length) {
      return;
    }

    // Remove all existing GIS service layers from the map to prevent ordering issues
    nextLoadedLayers.forEach(combinedLayer => {
      const { gisLayer } = combinedLayer;
      if (!gisLayer) return;
      const existingLayer = esriMap.findLayerById(gisLayer.id);
      if (existingLayer) {
        esriMap.remove(existingLayer);
      }
    });
    nextLoadedLayers.toReversed().forEach(combinedLayer => {
      const { gisLayer, active, sublayers } = combinedLayer;
      if (!gisLayer) return;

      gisLayer.visible = active;

      sublayers?.forEach(sl => {
        if (sl.gisSublayer) {
          sl.gisSublayer.visible = sl.active;
        }
      });

      esriMap.add(gisLayer, 0);
    });

    // Keep GeoComm indoor polygons at the bottom of operational layers so
    // point/line overlays (e.g. Axon) remain visible above them.
    const geocommIndoorLayers = esriMap.layers.items.filter(layer => {
      return layer?.id?.startsWith(GEOCOMM_INDOOR_MAPS_PREFIX);
    });
    geocommIndoorLayers.forEach(layer => {
      esriMap.reorder(layer, 0);
    });
    // Re-run when loaded layer IDs change (new layers finish loading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasGISServicesAccess,
    map?.mapInstance,
    loadedLayerIds,
    gisServicesLoading,
  ]);
  return (
    <EsriMapsContext.Provider value={map}>
      <MapCard
        hasEsriMapAccess={true}
        mapRef={mapRef}
        setMafError={setMafError}
        setADRError={setADRError}
      />
      <MapWrapper isResizing={isResizing}>
        <StyledMap ref={mapRef}>
          {map?.mapInstance && (
            <>
              <W3WMarkers map={map} isMapIconDisabled={isMapIconDisabled} />
              <AllAlertMarkers
                activeQueueTab={activeQueueTab}
                hoveredAlert={hoveredAlert}
                hoveredFrom={hoveredFrom}
                map={map}
                onMarkerAdd={onMarkerAdd}
                onMarkerHover={onMarkerHover}
                onMarkerClick={onMarkerClick}
                onRemoveID={onRemoveID}
                removedID={removedID}
                isMapIconDisabled={isMapIconDisabled}
              />
              <CallMarkers
                activeQueueTab={activeQueueTab}
                hoveredCall={hoveredCall}
                hoveredFrom={hoveredFrom}
                map={map}
                onMarkerAdd={handleCallMarkerAdd}
                onMarkerClick={handleCallMarkerClick}
                onMarkerHover={handleCallMarkerHover}
                onRemoveID={handleCallMarkerRemove}
                removedID={removedID}
                isMapIconDisabled={isMapIconDisabled}
              />
              <DeckGLNext
                map={map}
                removedID={removedID}
                activeQueueTab={activeQueueTab}
                isMapIconDisabled={isMapIconDisabled}
                mapRef={mapRef}
              />
              <GeoCommIndoorMaps map={map} />
              <TimeSliderComponent map={map} />
              {/*
              NOTE: this is NOT being used anywhere.
              disabled for now because of the new click to identify feature in DeckGLNext
              {hasClickToIdentifyAccess && (
                <>
                  <IdentifyMapImageLayer
                    mapInstance={map.mapInstance}
                    ref={mapRef}
                  />
                  <QueryFeatureLayer
                    mapInstance={map.mapInstance}
                    ref={mapRef}
                  />
                </>
              )} */}
              {themeName === DARK_THEME && (
                <Suspense fallback={<></>}>
                  <EsriDarkTheme />
                </Suspense>
              )}
            </>
          )}
        </StyledMap>
        {map?.mapInstance ? (
          <RecenterMapButton mapProvider={ESRI} mapInstance={map.mapInstance} />
        ) : (
          <></>
        )}
      </MapWrapper>
    </EsriMapsContext.Provider>
  );
};
